import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './styles.module.css';
import { Button, Dropdown, Table, Loading } from '../../../../common/ui';
import DateRangePicker from '../../../../common/components/DateRangePicker';
import { setBranchId } from '../../../auth/actions';
import { searchStockTransfers } from '../actions';

const TransferredStocks = ({
  auth: { branchList = [], branchId, companyId },
  setBranchId,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transfers, setTransfers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const page = parseInt(searchParams.get('page'), 10) || 1;
  const limit = parseInt(searchParams.get('limit'), 10) || 10;

  const getDefaultDates = () => {
    const today = new Date();
    return {
      fromDate: today.toLocaleDateString('en-CA'),
      toDate: today.toLocaleDateString('en-CA'),
    };
  };

  const defaultDates = getDefaultDates();
  const fromDate = searchParams.get('fromDate') || defaultDates.fromDate;
  const toDate = searchParams.get('toDate') || defaultDates.toDate;

  const branchOptions = useMemo(
    () =>
      (branchList || []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [branchList]
  );

  const fetchTransfers = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const res = await searchStockTransfers({
        companyId,
        start_date: fromDate,
        end_date: toDate,
        page,
        item_count: limit,
      });
      if (res?.error) {
        setTransfers([]);
        setPagination({});
        return;
      }
      setTransfers(Array.isArray(res?.data) ? res.data : []);
      setPagination(res?.pagination || {});
    } catch {
      setTransfers([]);
      setPagination({});
    } finally {
      setIsLoading(false);
    }
  }, [companyId, fromDate, toDate, page, limit]);

  useEffect(() => {
    const hasPage = searchParams.has('page');
    const hasLimit = searchParams.has('limit');
    const hasFromDate = searchParams.has('fromDate');
    const hasToDate = searchParams.has('toDate');
    if (!hasPage || !hasLimit || !hasFromDate || !hasToDate) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!hasPage) next.set('page', '1');
          if (!hasLimit) next.set('limit', '10');
          if (!hasFromDate) next.set('fromDate', defaultDates.fromDate);
          if (!hasToDate) next.set('toDate', defaultDates.toDate);
          return next;
        },
        { replace: true }
      );
    }
  }, []);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const handleDateRangeChange = (dateRange) => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const from = dateRange.startDate.toLocaleDateString('en-CA');
      const to = dateRange.endDate.toLocaleDateString('en-CA');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('fromDate', from);
        next.set('toDate', to);
        next.set('page', '1');
        return next;
      });
    }
  };

  const handleChangePage = (_, newPage) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(newPage + 1));
      return next;
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event?.target?.value, 10) || 10;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('limit', String(newLimit));
      next.set('page', '1');
      return next;
    });
  };

  const formatDate = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const columns = [
    { id: 'fromBranchName', label: 'Branch From', minWidth: 140 },
    { id: 'toBranchName', label: 'Branch To', minWidth: 140 },
    {
      id: 'costOfGoods',
      label: 'Cost of Goods',
      minWidth: 120,
      format: (v) => (v != null ? Number(v).toFixed(2) : '0.00'),
    },
    {
      id: 'salesValueOfGoods',
      label: 'Sales Value',
      minWidth: 120,
      format: (v) => (v != null ? Number(v).toFixed(2) : '0.00'),
    },
    {
      id: 'createdAt',
      label: 'Date',
      minWidth: 100,
      format: formatDate,
    },
  ];

  const rows = transfers.map((row) => ({
    id: row.id,
    fromBranchName: row.fromBranchName || row.fromBranchId || '-',
    toBranchName: row.toBranchName || row.toBranchId || '-',
    costOfGoods: row.costOfGoods,
    salesValueOfGoods: row.salesValueOfGoods,
    createdAt: row.createdAt,
  }));

  const totalRecords = pagination.totalRecords ?? 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>Transferred Stocks</h1>
          <p className={styles.breadcrumb}>Inventory / Transferred Stocks</p>
        </div>
        {branchId != null && branchOptions.length > 0 && (
          <div className={styles.branchWrap}>
            <Dropdown
              label="Branch"
              options={branchOptions}
              value={branchId}
              onChange={handleChangeBranch}
              placeholder="Select branch"
            />
          </div>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <Button onClick={() => navigate('/inventory/transfer-stocks/new')}>
              Transfer Stock
            </Button>
            <div className={styles.dateRangeWrap}>
              <DateRangePicker
                onDateRangeChange={handleDateRangeChange}
                initialStartDate={new Date(fromDate)}
                initialEndDate={new Date(toDate)}
              />
            </div>
          </div>

          <div className={styles.tableWrap}>
            {isLoading && <Loading variant="overlay" label="Loading..." />}
            <Table
              columns={columns}
              rows={rows}
              page={page - 1}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              totalCount={totalRecords}
              disableInternalPagination
              emptyMessage="No transferred stocks found."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

TransferredStocks.propTypes = {
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  setBranchId: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
});

export default connect(mapStateToProps, { setBranchId })(TransferredStocks);
